import React, { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";

const JURISDICTIONS = [
  { key: "US (FED)", label: "US (FED)" },
  { key: "US (STATE)", label: "US (STATE)" },
  { key: "UK", label: "UK" },
  { key: "EU", label: "EU" },
];

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * Map a linear slider [0..1000] to a value in [min..max] using an exponential curve.
 * This gives high precision at low values and avoids "jumping" too fast on the left.
 */
function sliderToExpValue(sliderPos, min, max) {
  const t = Math.min(1000, Math.max(0, Number(sliderPos))) / 1000;
  const minSafe = Math.max(1, Number(min));
  const maxSafe = Math.max(minSafe + 1, Number(max));
  const ratio = maxSafe / minSafe;
  return Math.round(minSafe * Math.pow(ratio, t));
}

/**
 * Invert sliderToExpValue to get slider position for an existing value.
 */
function expValueToSlider(value, min, max) {
  const v = Math.max(1, Number(value));
  const minSafe = Math.max(1, Number(min));
  const maxSafe = Math.max(minSafe + 1, Number(max));
  const ratio = maxSafe / minSafe;
  const t = Math.log(v / minSafe) / Math.log(ratio);
  const clamped = Number.isFinite(t) ? Math.min(1, Math.max(0, t)) : 0;
  return Math.round(clamped * 1000);
}

function fmtInt(n) {
  const x = Number(n);
  return Number.isFinite(x) ? x.toLocaleString() : "0";
}

export default function StartAudit() {
  const navigate = useNavigate();
  const location = useLocation();

  // If user is in /app/*, keep navigation inside /app/*
  const routePrefix = useMemo(
    () => (location.pathname.startsWith("/app") ? "/app" : ""),
    [location.pathname]
  );

  // Slider ranges (tune later if needed)
  const REVENUE_MIN = 10_000;
  const REVENUE_MAX = 50_000_000;

  const IMPACT_MIN = 10;
  const IMPACT_MAX = 1_000_000;

  const [formData, setFormData] = useState({
    clientName: "",
    industry: "",
    niche: "",
    annualTurnover: 5_000_000,
    userImpact: 100,
    jurisdictions: ["EU"], // multi-select; must never be empty
  });

  const hasFed = formData.jurisdictions.includes("US (FED)");
  const hasState = formData.jurisdictions.includes("US (STATE)");

  const toggleJurisdiction = useCallback((j) => {
    setFormData((prev) => {
      const selected = new Set(prev.jurisdictions);

      // If already selected -> deselect (but never allow empty)
      if (selected.has(j)) {
        selected.delete(j);
        if (selected.size === 0) selected.add("EU");
        return { ...prev, jurisdictions: Array.from(selected) };
      }

      // XOR rule between US (FED) and US (STATE)
      if (j === "US (FED)") selected.delete("US (STATE)");
      if (j === "US (STATE)") selected.delete("US (FED)");

      selected.add(j);
      return { ...prev, jurisdictions: Array.from(selected) };
    });
  }, []);

  const revenueSliderPos = useMemo(
    () => expValueToSlider(formData.annualTurnover, REVENUE_MIN, REVENUE_MAX),
    [formData.annualTurnover]
  );

  const impactSliderPos = useMemo(
    () => expValueToSlider(formData.userImpact, IMPACT_MIN, IMPACT_MAX),
    [formData.userImpact]
  );

  const isStartDisabled = useMemo(() => {
    const clientOk = String(formData.clientName || "").trim().length > 0;
    const industryOk = String(formData.industry || "").trim().length > 0;
    const nicheOk = String(formData.niche || "").trim().length > 0;
    const jurisdictionsOk =
      Array.isArray(formData.jurisdictions) && formData.jurisdictions.length > 0;
    const revenueOk = toNumber(formData.annualTurnover, 0) > 0;
    const impactOk = toNumber(formData.userImpact, 0) > 0;

    return !(clientOk && industryOk && nicheOk && jurisdictionsOk && revenueOk && impactOk);
  }, [formData]);

  const handleStart = useCallback(
    (e) => {
      e.preventDefault();

      const auditConfig = {
        clientName: String(formData.clientName || "").trim(),
        industry: String(formData.industry || "").trim(),
        niche: String(formData.niche || "").trim(),
        jurisdictions: formData.jurisdictions,
        annualTurnover: toNumber(formData.annualTurnover, 0),
        userImpact: toNumber(formData.userImpact, 0),
      };

      localStorage.setItem("aifine_config", JSON.stringify(auditConfig));

      // go to assessment inside the same namespace
      navigate(`${routePrefix}/assessment`, { replace: true });
    },
    [formData, navigate, routePrefix]
  );

  const cardShadow =
    "shadow-[0_10px_40px_-20px_rgba(15,23,42,0.30)]";

  return (
    <div className="h-screen overflow-hidden bg-slate-50 text-slate-900">
      <div className="flex h-full">
        <Sidebar />

        <main className="flex-1 overflow-auto">
          <div className="min-h-full px-6 py-10 md:px-10">
            <div className="mx-auto max-w-5xl">
              {/* Page header (no pill) */}
              <div className="mb-8">
                <h1 className="text-3xl font-semibold tracking-tight text-slate-900 md:text-4xl">
                  Start Audit
                </h1>
                <p className="mt-2 max-w-2xl text-sm text-slate-600">
                  Configure your assessment. These inputs initialize your audit and exposure model.
                </p>
              </div>

              {/* Card */}
              <div
                className={[
                  "rounded-3xl border border-slate-200 bg-white/90 backdrop-blur",
                  cardShadow,
                ].join(" ")}
              >
                {/* Top row */}
                <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5 md:px-8">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">
                      Assessment inputs
                    </div>
                    <div className="mt-1 text-xs text-slate-500">
                      Fields marked <span className="text-rose-500">*</span> must be completed before you can continue.
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => navigate(`${routePrefix}/dashboard`)}
                    className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50"
                  >
                    Back to Dashboard
                  </button>
                </div>

                <form onSubmit={handleStart} className="px-6 py-6 md:px-8 md:py-8">
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    {/* Client name (REQUIRED) */}
                    <div className="md:col-span-2">
                      <label className="mb-2 block text-xs font-medium text-slate-700">
                        Client Entity Name <span className="text-rose-500">*</span>
                      </label>
                      <input
                        required
                        className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-slate-300 focus:ring-4 focus:ring-blue-100"
                        placeholder="Legal entity name"
                        value={formData.clientName}
                        onChange={(e) =>
                          setFormData((p) => ({ ...p, clientName: e.target.value }))
                        }
                      />
                    </div>

                    {/* Industry */}
                    <div>
                      <label className="mb-2 block text-xs font-medium text-slate-700">
                        Industry <span className="text-rose-500">*</span>
                      </label>
                      <input
                        required
                        className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-slate-300 focus:ring-4 focus:ring-blue-100"
                        placeholder="e.g., Healthcare, Financial Services"
                        value={formData.industry}
                        onChange={(e) =>
                          setFormData((p) => ({ ...p, industry: e.target.value }))
                        }
                      />
                    </div>

                    {/* Niche */}
                    <div>
                      <label className="mb-2 block text-xs font-medium text-slate-700">
                        Niche <span className="text-rose-500">*</span>
                      </label>
                      <input
                        required
                        className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-slate-300 focus:ring-4 focus:ring-blue-100"
                        placeholder="e.g., Medical Billing, Lending, HR Screening"
                        value={formData.niche}
                        onChange={(e) =>
                          setFormData((p) => ({ ...p, niche: e.target.value }))
                        }
                      />
                    </div>

                    {/* Revenue slider */}
                    <div className="md:col-span-2">
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                        <div className="flex items-center justify-between gap-4">
                          <label className="text-xs font-semibold text-slate-700">
                            Annual Revenue Estimate (USD) <span className="text-rose-500">*</span>
                          </label>
                          <div className="text-lg font-extrabold text-blue-700">
                            ${fmtInt(formData.annualTurnover)}
                          </div>
                        </div>

                        <input
                          type="range"
                          min="0"
                          max="1000"
                          step="1"
                          className="mt-4 h-2 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-blue-600"
                          value={revenueSliderPos}
                          onChange={(e) => {
                            const v = sliderToExpValue(e.target.value, REVENUE_MIN, REVENUE_MAX);
                            setFormData((p) => ({ ...p, annualTurnover: v }));
                          }}
                        />
                      </div>
                    </div>

                    {/* Impact slider */}
                    <div className="md:col-span-2">
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                        <div className="flex items-center justify-between gap-4">
                          <label className="text-xs font-semibold text-slate-700">
                            People Impacted Per Month <span className="text-rose-500">*</span>
                          </label>
                          <div className="text-lg font-extrabold text-blue-700">
                            {fmtInt(formData.userImpact)}
                          </div>
                        </div>

                        <input
                          type="range"
                          min="0"
                          max="1000"
                          step="1"
                          className="mt-4 h-2 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-blue-600"
                          value={impactSliderPos}
                          onChange={(e) => {
                            const v = sliderToExpValue(e.target.value, IMPACT_MIN, IMPACT_MAX);
                            setFormData((p) => ({ ...p, userImpact: v }));
                          }}
                        />
                      </div>
                    </div>

                    {/* Jurisdictions */}
                    <div className="md:col-span-2">
                      <label className="mb-2 block text-xs font-medium text-slate-700">
                        Jurisdictions <span className="text-rose-500">*</span>
                      </label>

                      <div className="mb-3 text-xs text-slate-500">
                        You may select any combination{" "}
                        <span className="font-semibold text-slate-700">except</span>{" "}
                        <span className="font-semibold text-slate-700">US (FED)</span> and{" "}
                        <span className="font-semibold text-slate-700">US (STATE)</span> together.
                      </div>

                      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                        {JURISDICTIONS.map((j) => {
                          const isSelected = formData.jurisdictions.includes(j.key);
                          const isDisabled =
                            (j.key === "US (FED)" && hasState) ||
                            (j.key === "US (STATE)" && hasFed);

                          return (
                            <button
                              key={j.key}
                              type="button"
                              onClick={() => !isDisabled && toggleJurisdiction(j.key)}
                              aria-disabled={isDisabled}
                              className={[
                                "rounded-2xl border px-4 py-3 text-xs font-semibold transition",
                                isDisabled
                                  ? "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-300"
                                  : isSelected
                                  ? "border-blue-200 bg-blue-50 text-blue-800 shadow-sm"
                                  : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50",
                              ].join(" ")}
                            >
                              {j.label}
                            </button>
                          );
                        })}
                      </div>

                      <div className="mt-3 text-xs text-slate-500">
                        Selected:{" "}
                        <span className="font-semibold text-slate-700">
                          {formData.jurisdictions.join(", ")}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Footer actions */}
                  <div className="mt-8 flex flex-col-reverse gap-3 border-t border-slate-200 pt-6 md:flex-row md:items-center md:justify-between">
                    <div className="text-xs text-slate-500">
                      You can revise these settings later, but they will be used to initialize the assessment.
                    </div>

                    <button
                      type="submit"
                      disabled={isStartDisabled}
                      className={[
                        "inline-flex items-center justify-center rounded-2xl px-6 py-3 text-sm font-semibold transition",
                        isStartDisabled
                          ? "cursor-not-allowed bg-slate-200 text-slate-500"
                          : "bg-emerald-600 text-white shadow-sm hover:bg-emerald-700 active:scale-[0.99]",
                      ].join(" ")}
                    >
                      Start Detailed Assessment
                    </button>
                  </div>
                </form>
              </div>

              <div className="h-10" />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
